/**
 * De ING-ingang op favotrip.de (regie 25-10-2026; Dennis 19-10: "als we t over
 * duitsland gaat, dan hebben we t alleen over favotrip.de").
 *
 * Een ING-klant uit Duitsland doet alles op favotrip.de: geen doorverwijzing
 * naar favotrip.nl, inloggen en sessie op .de, Stripe-terugkeer op .de. Dit
 * bestand bevat alleen publieke regels, zonder serverafhankelijkheden, zodat
 * de middleware, de inlogpoort, de API-client (browser) en de routes onder
 * /api precies dezelfde regel gebruiken.
 *
 *   - `/exclusive` en `/exclusive/<naam>` geven op .de een 308 binnen .de naar
 *     `/ing` (src/middleware.js). Duitse hub- en dealpagina's zijn een vervolg.
 *   - `/ing` is op .de de ING-ingang (intern `ING_DE_INTERN_PAD`, lane 6+7):
 *     de collectie op slot, de actiecode, en met toegang de kaarten;
 *     `/ing/<slug of id>` is de hotelpagina (intern
 *     `ING_DE_INTERN_PAD/<segment>`, `ingDeInternPad`). Beide noindex en
 *     `private, no-store`. `/ing/presentation` en de Nederlandse ING-hub op
 *     favotrip.nl/ing blijven ongewijzigd.
 *   - De klantsessie staat op .de als host-only cookie, gezet door
 *     `/api/customer/auth/magic-link` (inwisselen met een nonce uit de
 *     host-only cookie `ft_ml_nonce`); de klant-API loopt op .de via de
 *     same-origin proxy `/api/klant-de/...`.
 *   - Geen Nederlandse vergelijkingsclaim op .de: `exclusiveClaimToegestaan`
 *     en `exclusiveDealVoorDomein` hieronder.
 *
 * Alles hierboven, behalve de claimregel, staat achter de schakelaar
 * `ING_DE_LIVE` (src/lib/luxe/ing-de-flags.js, standaard uit): gebruik
 * `ingDeAan(host)` / `ingDeAanInBrowser()` voor dat gedrag, niet
 * `isFavotripDeHost`. De claimregel geldt altijd: op .de nooit een
 * Nederlandse claim, ook met de schakelaar uit.
 */
import { DOMAIN_CONFIGS, normalizeHostname } from "@/lib/site-routing";
import { ingDeLive } from "./ing-de-flags";

/** Het domein van de Duitse markt. */
export const ING_DE_DOMEIN = "favotrip.de";

/** De publieke ING-ingang op .de (ook het standaard terugkeerpad na het inloggen). */
export const ING_DE_PAD = "/ing";

/** Het interne pad van de Duitse ING-ingang (de middleware herschrijft .de/ing hierheen). */
export const ING_DE_INTERN_PAD = "/ing-de-voordeur";

// Eén hotelsegment na /ing (slug of id), zoals de klant-proxy het doorlaat.
const ING_DE_HOTEL_SEG = /^[A-Za-z0-9-]{1,64}$/;
// Bestaande adressen onder /ing die nooit een hotelpagina zijn.
const ING_DE_GEEN_HOTEL = new Set(["presentation", "voorstelling", "praesentation"]);

/**
 * Het interne pad voor een publiek pad op .de: `/ing` -> de voordeur,
 * `/ing/<slug of id>` -> de hotelpagina `/ing-de-voordeur/<segment>`. Elk ander
 * pad (ook `/ing/presentation`): null.
 */
export function ingDeInternPad(pathname) {
  if (typeof pathname !== "string") return null;
  if (pathname === ING_DE_PAD) return ING_DE_INTERN_PAD;
  if (!pathname.startsWith(`${ING_DE_PAD}/`)) return null;
  const segment = pathname.slice(ING_DE_PAD.length + 1);
  if (!ING_DE_HOTEL_SEG.test(segment) || ING_DE_GEEN_HOTEL.has(segment.toLowerCase())) return null;
  return `${ING_DE_INTERN_PAD}/${segment}`;
}

/** De queryparameter op de voordeur na een mislukte inlog. */
export const ING_DE_INLOG_FOUT_PARAM = "inloggen";
export const ING_DE_INLOG_FOUT_WAARDE = "verlopen";

/** Waar een mislukte of verlopen inlog op .de landt. */
export const ING_DE_INLOG_FOUT_PAD = `${ING_DE_PAD}?${ING_DE_INLOG_FOUT_PARAM}=${ING_DE_INLOG_FOUT_WAARDE}`;

/** Host-only cookie met de nonce van een magic-link-aanvraag op .de. */
export const ML_NONCE_COOKIE = "ft_ml_nonce";

/** De nonce: 32 willekeurige bytes als 64 hextekens. */
export const ML_NONCE_RE = /^[0-9a-f]{64}$/;

/**
 * Geldigheid van de inloglink in seconden (Max-Age van `ft_ml_nonce`). Moet
 * gelijk zijn aan de geldigheid van het magic-link-token in de backend
 * (site-backend, lane 3). AANNAME: 15 minuten; wijzigt de backend die
 * geldigheid, dan gaat dit getal in dezelfde ronde mee.
 */
export const ML_NONCE_MAX_AGE_S = 15 * 60;

/** De same-origin proxy voor de klant-API op .de (src/app/api/klant-de/[...pad]/route.js). */
export const KLANT_DE_API_PAD = "/api/klant-de";

/** De inlogroute op .de (GET met ?token=...). */
export const MAGIC_LINK_DE_PAD = "/api/customer/auth/magic-link";

/** De bevestig-POST van de inlogroute op .de. */
export const MAGIC_LINK_DE_BEVESTIGEN_PAD = `${MAGIC_LINK_DE_PAD}/bevestigen`;

/**
 * true voor favotrip.de, www.favotrip.de en staging.favotrip.de (met of zonder
 * poort, hoofdletterongevoelig). Accepteert ook het kale site-contextdomein
 * "favotrip.de". Elke andere of lege host: false.
 */
export function isFavotripDeHost(hostOrDomain) {
  if (typeof hostOrDomain !== "string" || hostOrDomain.trim() === "") return false;
  const kaal = normalizeHostname(hostOrDomain).replace(/\.$/, "").replace(/^www\./, "").replace(/^staging\./, "");
  return kaal === ING_DE_DOMEIN;
}

/** Als `isFavotripDeHost`, voor de huidige browserhost (op de server: false). */
export function isFavotripDeInBrowser() {
  if (typeof window === "undefined") return false;
  try {
    return isFavotripDeHost(window.location.hostname);
  } catch {
    return false;
  }
}

/**
 * Geldt het .de-gedrag van de ING-ingang voor deze host of dit domein? Alleen
 * op favotrip.de en alleen met de schakelaar aan (src/lib/luxe/ing-de-flags.js).
 * Uit: favotrip.de gedraagt zich als op main.
 */
export function ingDeAan(hostOrDomain) {
  return ingDeLive() && isFavotripDeHost(hostOrDomain);
}

/** Als `ingDeAan`, voor de huidige browserhost (op de server: false). */
export function ingDeAanInBrowser() {
  return ingDeLive() && isFavotripDeInBrowser();
}

const KLANT_DE_SEG = "[a-z0-9-]{1,64}";

/**
 * De allowlist van de klant-proxy op .de (src/app/api/klant-de/[...pad]/route.js),
 * hier zodat de API-client (src/lib/customer-api.js) precies deze verzoeken via
 * de proxy stuurt en alle andere klantpaden (boekingen, vouchers, favorieten,
 * herinneringen, profiel wijzigen) zoals op main. `aanvraag`: een inlogaanvraag
 * (nonce + domein mee, nonce-cookie bij een 2xx). `sessie`: "verplicht" (zonder
 * cookie 401, geen fetch), "optioneel" of "geen". `query`: alleen bij de
 * /exclusive-*-paden. `exclusiveDe`: ook via de proxy als alleen Favotrip
 * Exclusive op .de aan staat (LUXE_DE_PUBLIEK_LIVE aan, ING_DE_LIVE uit;
 * fixronde 3 fe#1938): het aanmeldblok logt dan via dezelfde proxy en
 * inlogroute in, dus `me` en uitloggen moeten die host-only .de-sessie ook
 * herkennen en wissen.
 */
export const KLANT_DE_ROUTES = Object.freeze([
  { methods: ["GET"], pad: /^\/customer\/me$/, sessie: "verplicht", exclusiveDe: true },
  { methods: ["POST"], pad: /^\/customer\/auth\/logout$/, sessie: "optioneel", uitloggen: true, exclusiveDe: true },
  { methods: ["POST"], pad: /^\/customer\/auth\/magic-link\/request$/, sessie: "geen", aanvraag: true },
  { methods: ["POST"], pad: /^\/customer\/auth\/exclusive\/magic-link\/request$/, sessie: "geen", aanvraag: true },
  { methods: ["POST"], pad: /^\/exclusive-toegang\/aanmelden$/, sessie: "geen", aanvraag: true },
  { methods: ["GET", "POST"], pad: new RegExp(`^/exclusive-toegang(?:/${KLANT_DE_SEG}){1,2}$`), sessie: "optioneel", query: true },
  { methods: ["GET", "POST"], pad: new RegExp(`^/exclusive-ing(?:/${KLANT_DE_SEG}){1,2}$`), sessie: "optioneel", query: true },
]);

/**
 * De regel uit `KLANT_DE_ROUTES` voor deze methode en dit backendpad (zonder
 * query), of null. Een query telt alleen bij een regel met `query`.
 */
export function klantDeRoute(method, pad) {
  if (typeof pad !== "string" || pad === "") return null;
  const vraag = pad.indexOf("?");
  const kaal = vraag === -1 ? pad : pad.slice(0, vraag);
  const methode = typeof method === "string" ? method.toUpperCase() : "GET";
  const route = KLANT_DE_ROUTES.find((r) => r.methods.includes(methode) && r.pad.test(kaal)) || null;
  if (route && vraag !== -1 && !route.query) return null;
  return route;
}

// Alleen /ing, /ing/<segment>[/<segment>], /exclusive of /exclusive/<naam>:
// [A-Za-z0-9-] per segment. Geen query, geen fragment, geen `..`, geen `//`,
// geen backslash, geen domein. (/exclusive geeft op .de zelf een 308 naar /ing.)
const ING_DE_RETURN_TO = /^\/(?:ing(?:\/[A-Za-z0-9-]+){0,2}|exclusive(?:\/[A-Za-z0-9-]+)?)$/;

/** true als het pad een toegestaan terugkeerpad op .de is. */
export function isIngDeReturnTo(returnTo) {
  return typeof returnTo === "string" && ING_DE_RETURN_TO.test(returnTo);
}

/** Een veilig relatief terugkeerpad op .de, anders `/ing` (nooit een open redirect). */
export function safeIngDeReturnTo(returnTo) {
  return isIngDeReturnTo(returnTo) ? returnTo : ING_DE_PAD;
}

/**
 * De centrale claimregel voor Favotrip Exclusive: mag een bestaande
 * Exclusive-component op dit domein een vergelijkingsclaim tonen
 * (Booking-percentage, eurobesparing, registerterugval, hotelsiteclaim)?
 * Op favotrip.de nooit: die claims zijn Nederlandse teksten met Nederlandse
 * drempels. De ING-prijzen op .de komen later uit `/exclusive-ing/aanbod`
 * (lane 7), niet uit deze componenten.
 */
export function exclusiveClaimToegestaan(domain) {
  return !isFavotripDeHost(domain);
}

// De velden waaruit de bestaande componenten een claim afleiden: de live
// Booking-vergelijking (met de hotelsitevelden erin). Zonder dit veld is er
// ook geen registerterugval (readLuxeMeasuredClaim eist een live vergelijking).
const CLAIM_VELDEN = ["booking_comparison"];

/**
 * Een deal zonder claimbronnen als het domein geen claim toestaat; anders
 * precies dezelfde deal (zelfde referentie). Muteert nooit.
 */
export function exclusiveDealVoorDomein(deal, domain) {
  if (exclusiveClaimToegestaan(domain)) return deal;
  if (!deal || typeof deal !== "object" || Array.isArray(deal)) return deal;
  if (!CLAIM_VELDEN.some((veld) => Object.prototype.hasOwnProperty.call(deal, veld))) return deal;
  const kopie = { ...deal };
  for (const veld of CLAIM_VELDEN) delete kopie[veld];
  return kopie;
}

/** Als `exclusiveDealVoorDomein`, voor een lijst (de hub). */
export function exclusiveDealsVoorDomein(deals, domain) {
  if (exclusiveClaimToegestaan(domain) || !Array.isArray(deals)) return deals;
  return deals.map((deal) => exclusiveDealVoorDomein(deal, domain));
}

/**
 * Het gevalideerde site-adres voor een Host-header (Stripe-terugkeer): alleen
 * een host die een van onze domeinen is of daaronder valt
 * (www./staging./partner./zakelijk.), dan `https://<die host>`. Elke andere
 * host: null, nooit een stille terugval op favotrip.nl. Alleen buiten
 * productie mag localhost (http, met poort) voor lokaal ontwikkelen.
 *
 * @param {string | null | undefined} hostHeader
 * @param {{ production?: boolean }} [opties]
 * @returns {string | null}
 */
export function siteOriginVoorHost(hostHeader, { production = process.env.NODE_ENV === "production" } = {}) {
  if (typeof hostHeader !== "string" || hostHeader.trim() === "") return null;
  const raw = hostHeader.trim().toLowerCase();
  // Alleen een hostnaam met optionele poort; geen pad, gebruiker of schema.
  if (!/^[a-z0-9.-]+(?::\d{1,5})?$/.test(raw)) return null;
  const host = normalizeHostname(raw).replace(/\.$/, "");
  if (!production && (host === "localhost" || host === "127.0.0.1")) return `http://${raw}`;
  const bekend = DOMAIN_CONFIGS.some(({ domain }) => host === domain || host.endsWith(`.${domain}`));
  return bekend ? `https://${host}` : null;
}
